import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'
import { useAgentRunEvents } from '../../r3/composables/useAgentRunEvents'
import { useRefreshOnVisible } from '../../r3/composables/useRefreshOnVisible'
import { plural } from '../../r3/composables/useR3Text'
import {
  fetchStrategyPlay,
  runStrategyPlay,
  saveStrategyPlayBrief,
  type StrategyPlayChange,
  type StrategyPlayDetails,
  type StrategyPlayStep,
} from '../../r3/services/strategyApi'

export const BRIEF_LABEL = 'Who should this play find, and why?'
// The run's event stream shows its progress and reloads the play when it ends.
const RUNNING_POLL_MS = 30_000
const ACTIVE_STATUSES = new Set(['queued', 'running'])
const PROGRESS_SHOWN = 500

const STEP_LABELS: Record<string, string> = { email: 'Email', linkedin_connect: 'LinkedIn connect' }

function stepLabel(step: StrategyPlayStep): string {
  if (step.step_type === 'wait') return `wait ${step.wait_days} ${plural(step.wait_days ?? 0, 'day')}`
  return STEP_LABELS[step.step_type] ?? step.step_type
}

export function changeAuthor(change: StrategyPlayChange): string {
  return change.by?.name ?? 'Strategy agent'
}

export function changeLabel(change: StrategyPlayChange): string {
  if (change.kind === 'create_play') return 'Made the play'
  if (change.kind === 'edit_play') return 'Changed the brief'
  if (change.kind === 'retire_play') return 'Retired the play'
  if (change.kind === 'edit_sequence') {
    if (change.removed) return 'Removed the steps'
    if (change.steps) return `Changed the steps: ${change.steps.map(stepLabel).join(', ')}`
    return 'Changed the sequence settings'
  }
  return change.kind
}

export function isActiveRun(run: { status: string } | null | undefined): boolean {
  return !!run && ACTIVE_STATUSES.has(run.status)
}

// A Strategy play: its brief, notes, counts and runs, with the latest run's live progress.
export function useStrategyPlay(playId: MaybeRefOrGetter<string>) {
  const details = ref<StrategyPlayDetails | null>(null)
  const error = ref('')
  const saving = ref(false)

  const latestRun = computed(() => details.value?.runs[0] ?? null)
  const running = computed(() => isActiveRun(latestRun.value))
  const { events, latestProgress, done } = useAgentRunEvents(() => latestRun.value?.id ?? null)
  const progress = computed(() =>
    events.value.filter((e) => e.kind === 'progress' && e.message).slice(-PROGRESS_SHOWN).map((e) => e.message as string),
  )

  async function load() {
    try {
      details.value = await fetchStrategyPlay(toValue(playId))
      error.value = ''
    } catch (e: any) {
      error.value = e?.message || 'Failed to load the play'
    }
  }

  async function send(request: () => Promise<unknown>, failure: string) {
    saving.value = true
    error.value = ''
    try {
      await request()
      await load()
    } catch (e: any) {
      error.value = e?.message || failure
    } finally {
      saving.value = false
    }
  }

  function save(brief: string) {
    return send(() => saveStrategyPlayBrief(toValue(playId), brief, false), 'Failed to save the brief')
  }

  function saveAndRun(brief: string) {
    return send(() => saveStrategyPlayBrief(toValue(playId), brief, true), 'Failed to start the play')
  }

  function runNow() {
    return send(() => runStrategyPlay(toValue(playId)), 'Failed to start the play')
  }

  useRefreshOnVisible(load, { intervalMs: () => (running.value ? RUNNING_POLL_MS : 0) })
  watch(done, (finished) => { if (finished) void load() })
  watch(() => toValue(playId), () => { void load() })

  return { details, error, saving, latestRun, running, latestProgress, progress, save, saveAndRun, runNow, reload: load }
}
